<script setup lang="ts">
/**
 * Step 1 — Entity information (BR02A): FYE, registered office address,
 * office hours, entity email. Mirrors test_incorporation.html step 3.
 */
import { computed } from 'vue';
import SgAddressFields from '@/features/setupEntity/components/sg/shared/SgAddressFields.vue';
import SgJurisdictionDateField from '@/features/setupEntity/components/sg/shared/SgJurisdictionDateField.vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import type { EfEntityInfo } from '../composables/incorporationEfilingModel';

const props = defineProps<{
  entityInfo: EfEntityInfo;
  readonly?: boolean;
}>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const tr = (key: string, fallback: string) => t(`startTask.sgIncorporationEfiling.${key}`) || fallback;

const is3to5 = computed(() => props.entityInfo.operatingHourType === '3TO5');

// 회사 이메일 필수 + 형식 검증(다국어).
const emailRules = [
  (v: string) => !!String(v ?? '').trim() || tr('entityEmailRequired', 'Enter the entity email.'),
  (v: string) =>
    !String(v ?? '').trim() ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim()) ||
    tr('entityEmailInvalid', 'Enter a valid email address.'),
];
</script>

<template>
  <div class="ef-step">
    <div class="ef-step__section">
      <div class="ef-step__h">Financial Year End (FYE)</div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <SgJurisdictionDateField
            v-model="entityInfo.finYearEnd"
            label="Financial year end date"
            :readonly="readonly"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="entityInfo.finYearPeriod"
            :items="[
              { title: '12 months', value: '12M' },
              { title: '52 weeks accounting period', value: '52W' },
            ]"
            label="Financial year period"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="ef-step__section">
      <div class="ef-step__h">Registered office address</div>
      <SgAddressFields
        :model-value="entityInfo.registeredAddress"
        :readonly="readonly"
        local-only
        show-level-unit-toggle
        strict
        @update:model-value="entityInfo.registeredAddress = $event"
      />
    </div>

    <div class="ef-step__section">
      <div class="ef-step__h">Office hours</div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-select
            v-model="entityInfo.operatingHourType"
            :items="[
              { title: 'At least 3 hours but less than 5 hours', value: '3TO5' },
              { title: 'At least 5 hours', value: 'OVER5' },
            ]"
            label="Office hours"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
        <v-col v-if="is3to5" cols="12" sm="6">
          <v-select
            v-model="entityInfo.operatingHours"
            :items="['3', '4']"
            label="Number of working hours"
            density="compact"
            variant="outlined"
            hide-details
            :readonly="readonly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="ef-step__section">
      <div class="ef-step__h">Entity email</div>
      <v-text-field
        v-model="entityInfo.email"
        label="Email"
        density="compact"
        variant="outlined"
        :hide-details="false"
        :rules="emailRules"
        :readonly="readonly"
      />
    </div>
  </div>
</template>

<style scoped>
.ef-step__section {
  margin-bottom: 18px;
}
.ef-step__h {
  font-size: 14px;
  font-weight: 700;
  color: #1a2336;
  margin-bottom: 8px;
}
</style>
